import { useLingui } from "@lingui/react"
import { Plural, Trans } from "@lingui/react/macro"
import { IconChevronDown, IconMicrophone, IconRefresh, IconUsers, IconWifiOff } from "@tabler/icons-react"
import { useMemo, useState } from "react"
import { speakerDisplayLabel, speakerVMFromName } from "../../lib/speaker-vm"
import { TypewriterText } from "../TypewriterText"
import classes from "./CaptureMonitor.module.css"
import { UNANSWERED_SPEECH_STALE_SEC } from "./hearing"
import { type TMicLevel, Visualizer } from "./Visualizer"

interface ILastLine {
  speaker: string
  text: string
}

interface IProps {
  level: TMicLevel
  volume: number
  /** Per-frequency band magnitudes (0..1) driving the live visualiser. */
  spectrum?: number[]
  monitoring: boolean
  /** Distinct speakers detected so far. */
  speakerCount: number
  /**
   * The single most recent finalised line — the current-line ticker. null
   * before the first words land.
   */
  lastLine: ILastLine | null
  /**
   * Seconds of speech with no interim or final text (ADR 0036). The card turns
   * amber at {@link UNANSWERED_SPEECH_STALE_SEC}. null = the caller is not
   * tracking it, so the card stays quiet.
   */
  secondsSinceLastWord: number | null
  paused: boolean
  /**
   * The live transcript, revealed inline by the centre pull-tab. When omitted
   * the tab is hidden (e.g. stories that only exercise the capture surface).
   */
  transcript?: React.ReactNode
  /**
   * When offline: audio is captured + held locally, but the live transcript and
   * insights can't run until reconnect. Shows a reassuring "recording only" note.
   */
  offlineRecordingOnly?: boolean
  /**
   * The live-transcription WebSocket is in its reconnect loop — the transcript is
   * frozen but capture is unaffected. Shows a calm "reconnecting" reassurance.
   */
  wsReconnecting?: boolean
}

/**
 * CaptureMonitor — the live "proof of capture" surface during recording, built
 * to mirror the pre-record "Microphone check" card: a titled header with a Live
 * indicator, the voice count and the current-line ticker beneath it, then the
 * full-width waveform. The transcript starts open; the pull-tab hides it.
 * Amber means speech went unanswered, not that the clock moved. Pure + dumb
 * (only the open/closed UI state).
 */
export function CaptureMonitor({
  level,
  volume,
  spectrum,
  monitoring,
  speakerCount,
  lastLine,
  secondsSinceLastWord,
  paused,
  transcript,
  offlineRecordingOnly = false,
  wsReconnecting = false,
}: IProps): React.JSX.Element {
  const { i18n } = useLingui()
  const [expanded, setExpanded] = useState(true)
  const stale = !paused && secondsSinceLastWord !== null && secondsSinceLastWord >= UNANSWERED_SPEECH_STALE_SEC
  const transcriptOpen = Boolean(transcript) && expanded
  const tickerLabel = useMemo(
    () => (lastLine ? speakerDisplayLabel(speakerVMFromName(lastLine.speaker), i18n) : ""),
    [lastLine, i18n],
  )

  return (
    <div
      className={classes.monitor}
      data-paused={paused ? "true" : "false"}
      data-stale={stale ? "true" : "false"}
      data-expanded={expanded ? "true" : "false"}
      data-testid="capture-monitor"
    >
      <div className={classes.head}>
        <span className={classes.title}>
          <IconMicrophone size={16} color="var(--mantine-color-brand-5)" />
          <Trans>Capturing</Trans>
        </span>
        <span className={`${classes.live} ${paused ? "" : classes.liveActive}`}>
          <span className={paused ? classes.liveDotIdle : classes.liveDotActive} aria-hidden="true" />
          {paused ? <Trans>Paused</Trans> : <Trans>Live</Trans>}
        </span>
      </div>

      {offlineRecordingOnly && (
        <div className={classes.offlineNotice} role="status">
          <IconWifiOff size={13} />
          <Trans>Recording only — transcription and insights will generate after you reconnect.</Trans>
        </div>
      )}

      {!offlineRecordingOnly && wsReconnecting && (
        <div className={classes.reconnectNotice} role="status" data-testid="reconnect-notice">
          <IconRefresh size={13} className={classes.reconnectIcon} />
          <Trans>Reconnecting live transcription… your audio is still recording.</Trans>
        </div>
      )}

      <span className={classes.meta}>
        <IconUsers size={12} />
        <Plural value={speakerCount} one="# voice so far" other="# voices so far" />
      </span>

      {stale ? (
        <p className={classes.staleNotice} role="status" data-testid="transcript-stale">
          <Trans>We hear you, but no words are coming through. Check the microphone.</Trans>
        </p>
      ) : null}

      {!transcriptOpen && lastLine ? (
        <p className={classes.ticker} aria-hidden="true" data-testid="capture-ticker">
          <span className={classes.tickerSpeaker}>
            <TypewriterText value={tickerLabel} />:
          </span>{" "}
          <span className={classes.tickerText}>{lastLine.text}</span>
        </p>
      ) : null}

      <Visualizer level={level} volume={volume} spectrum={spectrum} monitoring={monitoring} showQuietHint={false} />

      {transcript ? (
        <>
          <button
            type="button"
            className={classes.pullTab}
            onClick={() => setExpanded((e) => !e)}
            aria-expanded={expanded}
          >
            {expanded ? <Trans>Hide transcript</Trans> : <Trans>Show transcript</Trans>}
            <IconChevronDown size={13} className={classes.chevron} />
          </button>
          <div className={classes.transcriptWrap}>
            <div className={classes.transcriptInner}>{transcript}</div>
          </div>
        </>
      ) : null}
    </div>
  )
}
